import {
  ArrowUpRight,
  Brain,
  CheckCircle2,
  CircleDashed,
  Code2,
  Database,
  ExternalLink,
  FileText,
  Gauge,
  MessageCircle,
  ScrollText,
  Sparkles,
  Wrench,
  type LucideIcon,
} from 'lucide-react';
import { GlassPanel } from '../components/GlassPanel';
import { C } from '../lib/tokens';
import type { ViewKey } from '../components/TopMenu';
import * as argoWorker from '../lib/agent/argoWorker';
import * as enrich from '../lib/agent/enrich';
import * as meetNotes from '../lib/agent/meetNotes';
import * as slackNotify from '../lib/agent/slackNotify';

/**
 * In-app tool that navigates to a view.
 */
interface InAppTool {
  type: 'in-app';
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  route: ViewKey;
  accent: 'magenta' | 'orange' | 'blue' | 'green';
}

/**
 * Worker seam: external service with live Connected/Simulation status.
 */
interface WorkerTool {
  type: 'worker';
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  accent: 'magenta' | 'orange' | 'blue' | 'green';
  /** Returns { configured, baseUrl } for status pill */
  getInfo: () => { configured: boolean; baseUrl: string | null };
}

/**
 * External link that opens in a new window.
 */
interface ExternalTool {
  type: 'external';
  id: string;
  name: string;
  description: string;
  icon: LucideIcon;
  url: string;
  accent: 'magenta' | 'orange' | 'blue' | 'green';
}

type ToolDef = InAppTool | WorkerTool | ExternalTool;

const tools: ToolDef[] = [
  // In-app tools (onLaunch)
  {
    type: 'in-app',
    id: 'chat',
    name: 'Chat agent',
    description: 'Hand off work to Claude. Ask about accounts, run analyses, generate content.',
    icon: MessageCircle,
    route: 'chat',
    accent: 'magenta',
  },
  {
    type: 'in-app',
    id: 'content-engine',
    name: 'Content Engine',
    description: 'Generate blogs, proposals, internal comms, and on-brand decks from a brief.',
    icon: Sparkles,
    route: 'content-engine',
    accent: 'magenta',
  },
  {
    type: 'in-app',
    id: 'scoring-engine',
    name: 'Scoring Engine',
    description: 'Live scoring config + analytics — lead heat, deal value, and customer health bands.',
    icon: Gauge,
    route: 'scoring-engine',
    accent: 'orange',
  },
  {
    type: 'in-app',
    id: 'files',
    name: 'Data room',
    description: 'Unified file store for every deal. Organize, share, and search proposals, contracts, and docs.',
    icon: Database,
    route: 'files',
    accent: 'blue',
  },
  {
    type: 'in-app',
    id: 'contracts',
    name: 'Contracts',
    description: 'Proposals and contracts attached to every prospect and account. Draft, edit, sign, track.',
    icon: ScrollText,
    route: 'contracts',
    accent: 'orange',
  },
  {
    type: 'in-app',
    id: 'tickets',
    name: 'Tickets',
    description: 'Engineering backlog. Specs, owners, estimates, deadlines, and an append-only timeline.',
    icon: FileText,
    route: 'tickets',
    accent: 'green',
  },
  // Worker seams
  {
    type: 'worker',
    id: 'agent-worker',
    name: 'Claude agent worker',
    description: 'Chat, autopilot, and deployed agents.',
    icon: Brain,
    accent: 'magenta',
    getInfo: argoWorker.workerInfo,
  },
  {
    type: 'worker',
    id: 'enrichment',
    name: 'Prospect enrichment',
    description: 'LinkedIn + website evaluation for lead scoring.',
    icon: Gauge,
    accent: 'blue',
    getInfo: enrich.enrichInfo,
  },
  {
    type: 'worker',
    id: 'meet-notes',
    name: 'Google Workspace',
    description: 'Pulls Google Meet notes into the activity timeline.',
    icon: Code2,
    accent: 'green',
    getInfo: meetNotes.meetInfo,
  },
  {
    type: 'worker',
    id: 'slack-notify',
    name: 'Slack notifications',
    description: 'Posts digests and alerts to team Slack channels.',
    icon: Wrench,
    accent: 'orange',
    getInfo: slackNotify.slackInfo,
  },
  // External links
  {
    type: 'external',
    id: 'argoiq',
    name: 'Argo IQ',
    description: 'Product site: positioning, AQI, and the operating-layer pitch.',
    icon: ExternalLink,
    url: 'https://argoiq.com',
    accent: 'magenta',
  },
  {
    type: 'external',
    id: 'proposals',
    name: 'Proposal reference',
    description: 'The branded proposal layout our generated docs match.',
    icon: ExternalLink,
    url: 'https://proposals.argoiq.com/fusiontaxes',
    accent: 'magenta',
  },
  {
    type: 'external',
    id: 'personas',
    name: 'Persona reference',
    description: 'The buyer-persona format the persona creator targets.',
    icon: ExternalLink,
    url: 'https://trenton.personas.argoiq.com',
    accent: 'blue',
  },
  {
    type: 'external',
    id: 'os',
    name: 'Argo OS workspace',
    description: 'This app — the in-house super-CRM and agent control plane.',
    icon: ExternalLink,
    url: 'https://os.argoiq.com',
    accent: 'green',
  },
];

const accentToBg: Record<ToolDef['accent'], string> = {
  magenta: 'linear-gradient(135deg, rgba(255,0,255,0.22), rgba(187,65,152,0.14))',
  orange: 'linear-gradient(135deg, rgba(253,173,1,0.22), rgba(226,57,57,0.12))',
  blue: 'linear-gradient(135deg, rgba(0,130,243,0.22), rgba(47,1,253,0.14))',
  green: 'linear-gradient(135deg, rgba(34,197,94,0.22), rgba(2,122,72,0.14))',
};

const accentToColor: Record<ToolDef['accent'], string> = {
  magenta: C.magentaSoft,
  orange: '#ffd06b',
  blue: '#7ec1ff',
  green: '#6ee7a4',
};

interface ToolsViewProps {
  /** Hand-off to App-level routing; called when a tool tile that has a
   *  dedicated route is clicked. Tiles without a route do nothing yet. */
  onLaunch?: (key: ViewKey) => void;
}

/**
 * Control panel launcher hub. Grid of tool cards with icon, name, description,
 * and action (launch for in-app tools, Connected/Simulation pill for workers,
 * external link for URLs). Feels like a unified dashboard, not a placeholder.
 */
export function ToolsView({ onLaunch }: ToolsViewProps = {}) {
  // Segment tools by type for logical grouping
  const inAppTools = tools.filter((t) => t.type === 'in-app');
  const workerTools = tools.filter((t) => t.type === 'worker');
  const externalTools = tools.filter((t) => t.type === 'external');

  return (
    <div className="app-container" style={{ display: 'grid', gap: 24 }}>
      <header>
        <span className="t-eyebrow">Tools</span>
        <h1 className="t-page-title" style={{ margin: '6px 0 0' }}>Workspace control panel</h1>
        <p style={{ margin: '4px 0 0', color: C.textMuted }} className="t-body">
          In-app tools, worker seams with live status, and external references.
        </p>
      </header>

      {/* In-app tools */}
      {inAppTools.length > 0 && (
        <section>
          <div style={{ marginBottom: 12 }}>
            <span className="t-eyebrow">In-app tools</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>Navigate and launch</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {inAppTools.map((t) => {
              const tool = t as InAppTool;
              const Icon = tool.icon;
              return (
                <ToolCard
                  key={tool.id}
                  icon={Icon}
                  accent={tool.accent}
                  name={tool.name}
                  description={tool.description}
                  action="Launch"
                  onAction={() => onLaunch?.(tool.route)}
                  pill={null}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* Worker seams */}
      {workerTools.length > 0 && (
        <section>
          <div style={{ marginBottom: 12 }}>
            <span className="t-eyebrow">Worker seams</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>Live configuration status</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {workerTools.map((t) => {
              const tool = t as WorkerTool;
              const Icon = tool.icon;
              const info = tool.getInfo();
              const pill = info.configured
                ? { label: 'Connected', variant: 'glass-pill glass-pill--green' }
                : { label: 'Simulation', variant: 'glass-pill' };
              return (
                <ToolCard
                  key={tool.id}
                  icon={Icon}
                  accent={tool.accent}
                  name={tool.name}
                  description={tool.description}
                  action={null}
                  onAction={() => {}}
                  pill={pill}
                />
              );
            })}
          </div>
        </section>
      )}

      {/* External references */}
      {externalTools.length > 0 && (
        <section>
          <div style={{ marginBottom: 12 }}>
            <span className="t-eyebrow">External references</span>
            <h2 className="t-section-title" style={{ margin: '4px 0 0' }}>Links and documentation</h2>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: 12 }}>
            {externalTools.map((t) => {
              const tool = t as ExternalTool;
              const Icon = tool.icon;
              return (
                <ToolCard
                  key={tool.id}
                  icon={Icon}
                  accent={tool.accent}
                  name={tool.name}
                  description={tool.description}
                  action="Open"
                  onAction={() => window.open(tool.url, '_blank', 'noopener,noreferrer')}
                  pill={null}
                />
              );
            })}
          </div>
        </section>
      )}
    </div>
  );
}

/**
 * Reusable tool card with icon, name, description, optional pill, and action.
 */
function ToolCard({
  icon: Icon,
  accent,
  name,
  description,
  action,
  onAction,
  pill,
}: {
  icon: LucideIcon;
  accent: ToolDef['accent'];
  name: string;
  description: string;
  action: string | null;
  onAction: () => void;
  pill: { label: string; variant: string } | null;
}) {
  return (
    <GlassPanel
      variant="default"
      className="tool-tile"
      style={{
        padding: '20px',
        display: 'flex',
        flexDirection: 'column',
        gap: 12,
        cursor: action ? 'pointer' : 'default',
        transition: 'transform 240ms cubic-bezier(0.4,0,0.2,1), box-shadow 240ms ease',
      }}
      onClick={() => action && onAction()}
      role={action ? 'button' : undefined}
      tabIndex={action ? 0 : undefined}
      onKeyDown={
        action
          ? (e) => {
              // Match the house keyboard pattern: actionable cards fire on
              // Enter/Space so they are reachable without a mouse. Worker/info
              // cards (action === null) stay non-interactive.
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onAction();
              }
            }
          : undefined
      }
      onMouseEnter={(e) => {
        if (action) {
          e.currentTarget.style.transform = 'translateY(-3px)';
          e.currentTarget.style.boxShadow =
            '0 32px 80px rgba(0,0,0,0.45), 0 12px 30px rgba(0,0,0,0.25), inset 0 1px 0 rgba(255,255,255,0.28)';
        }
      }}
      onMouseLeave={(e) => {
        e.currentTarget.style.transform = '';
        e.currentTarget.style.boxShadow = '';
      }}
    >
      {/* Icon + accent background */}
      <div
        style={{
          width: 48,
          height: 48,
          borderRadius: 12,
          background: accentToBg[accent],
          border: '1px solid rgba(255,255,255,0.14)',
          display: 'grid',
          placeItems: 'center',
          color: accentToColor[accent],
          boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.18)',
        }}
      >
        <Icon size={24} strokeWidth={1.75} />
      </div>

      {/* Name + description */}
      <div style={{ flex: 1 }}>
        <h3 className="t-section-title" style={{ margin: '0 0 4px' }}>
          {name}
        </h3>
        <p className="t-body" style={{ margin: 0, color: C.textMuted }}>
          {description}
        </p>
      </div>

      {/* Action button or status pill */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
        {pill && <span className={pill.variant} style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
          {pill.label === 'Connected' ? <CheckCircle2 size={12} /> : <CircleDashed size={12} />}
          {pill.label}
        </span>}
        {action && (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: accentToColor[accent], fontSize: 12, fontWeight: 600, marginLeft: 'auto' }}>
            {action} <ArrowUpRight size={12} />
          </span>
        )}
      </div>
    </GlassPanel>
  );
}
